import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AddonBin, AddonStation, ItemDetailAddon } from "app/model/entities/entity-model";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import { interval } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-handheld-bins",
  templateUrl: "./handheld-bins.component.html",
  standalone: false,
})
export class HandheldBinsComponent extends DomainBaseComponent {
  addonBins: AddonBin[] = [];
  addonStations: AddonStation[] = [];
  selectedStationId = 0;
  showClosedBins = false;
  showRefreshIcon = false;

  private static readonly STATION_STORAGE_KEY = "hh-selected-station";
  private readonly REFRESH_SECONDS = 90;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute
  ) {
    super(domainService);
  }

  override async ngOnInit() {
    super.ngOnInit();
    // Subscribe before the first await. Started after the load, a page left during the load
    // (Home or a bin tapped on a slow handheld) was already destroyed, nothing stopped the timer,
    // and every tick cleared the ItemDetailAddons that Process Bin was showing.
    interval(this.REFRESH_SECONDS * 1000)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.autoRefresh());

    this.addonStations = await this.dbQueryService.getAll(AddonStation);
    const saved = localStorage.getItem(HandheldBinsComponent.STATION_STORAGE_KEY);
    if (saved) {
      this.selectedStationId = +saved;
    }
    await this.loadBins();
    this.isPageReady = true;
  }

  private async autoRefresh() {
    if (!this.isPageReady) return;
    this.showRefreshIcon = true;
    await this.loadBins();
    this.showRefreshIcon = false;
  }

  @wrapBusy
  async loadBins() {
    this.uow.clearEntities(AddonBin);
    this.uow.clearEntities(ItemDetailAddon);
    let bins = await this.dbQueryService.getAddonBins(this.showClosedBins);
    if (this.selectedStationId !== 0) {
      bins = bins.filter(
        (b) =>
          b.pendingAddonStationId === this.selectedStationId ||
          b.currentAddonStationId === this.selectedStationId
      );
    }
    this.addonBins = _.orderBy(bins, [(b) => b.priorityOrder, (b) => b.account?.accountName], ["asc", "desc"]);
  }

  async onFilterChanged() {
    localStorage.setItem(HandheldBinsComponent.STATION_STORAGE_KEY, String(this.selectedStationId));
    await this.loadBins();
  }

  processBin(bin: AddonBin) {
    this.router.navigate(["/handheld/process-bin", bin.id]);
  }

  goBack() {
    this.router.navigate(["/handheld/home"]);
  }

  getStationStatusBadgeClass(bin: AddonBin): string {
    if (!bin.currentAddonBinStationStatusId) return "hh-badge-pending";
    const name = bin.currentAddonBinStationStatus?.name?.toLowerCase() || "";
    if (name.includes("process")) return "hh-badge-in-process";
    if (name.includes("hold")) return "hh-badge-on-hold";
    if (name.includes("closed") || name.includes("complete")) return "hh-badge-complete";
    if (name.includes("await")) return "hh-badge-await";
    return "hh-badge-pending";
  }
}
